import { PageTitle } from "@/components/custom/PageTitle"
import { WorkflowsDataTable } from "./Table/Workflows/WorkflowsDataTable"
import { workflowsColumns } from "./Table/Workflows/WorkflowsColumns"
import { useSelector } from "react-redux"
import { RootState } from "@/redux"
import { Switch } from "@/components/ui/switch"
import { useEffect, useMemo, useState } from "react"
import { WorkflowStatus } from "@/constants/workflows/workflows"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Delete } from "lucide-react"
import { ButtonVariant } from "@/constants/button/button"
import { Pagination } from "@/components/custom/Pagination/Pagination"
import { INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname"

const DEFAULT_PAGE_SIZE = 10

export const AllWorkflowsRun = () => {
  const { workflows } = useSelector((state: RootState) => state.workflow)
  const [hiddenWorkflows, showHiddenWorkflows] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [paginationState, setPaginationState] = useState({ limit: DEFAULT_PAGE_SIZE, offset: 0 })

  const visibleWorkflows = useMemo(() => {
    return (hiddenWorkflows
      ? workflows
      : workflows.filter(w => w.status !== WorkflowStatus.DELETED))
      .slice()
      .sort((a, b) => {
        const aTime = a.dateCreated ? new Date(a.dateCreated).getTime() : 0
        const bTime = b.dateCreated ? new Date(b.dateCreated).getTime() : 0
        return bTime - aTime
      })
  }, [hiddenWorkflows, workflows])

  const filteredWorkflows = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) return visibleWorkflows
    return visibleWorkflows.filter(workflow => (workflow.nickname ?? "").toLowerCase().includes(normalizedQuery))
  }, [visibleWorkflows, searchQuery])

  useEffect(() => {
    setPaginationState(prev => {
      const total = filteredWorkflows.length
      if (total === 0) {
        return prev.offset === 0 ? prev : { ...prev, offset: 0 }
      }
      const lastPageOffset = Math.floor((total - 1) / prev.limit) * prev.limit
      if (prev.offset > lastPageOffset) {
        return { ...prev, offset: lastPageOffset }
      }
      return prev
    })
  }, [filteredWorkflows.length, paginationState.limit])

  const paginatedWorkflows = useMemo(() => {
    const start = paginationState.offset
    const end = start + paginationState.limit
    return filteredWorkflows.slice(start, end)
  }, [filteredWorkflows, paginationState])

  return (
    <div className="protectedPageLayout">
      <PageTitle title="Workflow History" />
      <div className="w-full flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
        <div className="flex w-full items-center gap-2 md:max-w-md relative">
          <Input
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value)
              setPaginationState(prev => ({ ...prev, offset: 0 }))
            }}
            placeholder="Search workflows"
            className={`${INPUT_FIELD_CLASSNAME} bg-foreground text-primary`}
            aria-label="Search workflows"
          />
          {searchQuery && (
            <Button
              type="button"
              variant={ButtonVariant.ICON}
              className="h-9 text-destructive rounded rounded-md bg-destructive-foreground absolute right-[2px]"
              onClick={() => setSearchQuery("")}
              disabled={!searchQuery.trim()}
            >
              <Delete size={16} />
            </Button>
          )}
        </div>
        <div className="w-full justify-end flex items-center md:w-auto">
          <p className="text-primary mr-2 text-sm">{hiddenWorkflows ? 'Hide' : 'Show'} Deleted Workflows</p>
          <Switch
            className="cursor-pointer"
            checked={hiddenWorkflows}
            onCheckedChange={() => showHiddenWorkflows(!hiddenWorkflows)}
          />
        </div>
      </div>
      <WorkflowsDataTable columns={workflowsColumns} data={paginatedWorkflows} />
      <Pagination
        total={filteredWorkflows.length}
        limit={paginationState.limit}
        offset={paginationState.offset}
        onPageChange={(newOffset) => setPaginationState(prev => ({ ...prev, offset: newOffset }))}
        onLimitChange={(newLimit) => setPaginationState({ limit: newLimit, offset: 0 })}
      />
    </div>
  )
}
